import { CircleNotch, FolderPlus } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { usePromoteChatToProject } from "../../../utils/queries/askPunttProjects";

// Registered route id for the project workspace. Kept as a literal (instead of
// importing campaignRoute) so this leaf component carries no route-module
// import — campaignRoute imports ./campaign, which would create a cycle.
const PROJECT_ROUTE_ID = "/ask-puntt/projects/$projectId";

/**
 * "Create project from this chat" affordance (§2 / Journey B). Promotes a
 * standalone chat into a new AskPunttProject (idempotent server-side) and
 * navigates into the new project's feed workspace on success.
 */
export function PromoteToProjectButton({
  chatId,
  onPromoted,
}: {
  chatId: string;
  /** Optional override; defaults to navigating into the new project. */
  onPromoted?: (projectId: string) => void;
}) {
  const navigate = useNavigate();
  const promote = usePromoteChatToProject();
  const { t } = useTranslation("ui");

  const handleClick = async () => {
    try {
      const result = await promote.mutateAsync(chatId);
      const projectId = result.projectId;
      if (onPromoted) {
        onPromoted(projectId);
      } else {
        navigate({
          to: PROJECT_ROUTE_ID,
          params: { projectId },
          search: { chatId: undefined },
        });
      }
    } catch {
      // usePromoteChatToProject surfaces an error toast.
    }
  };

  return (
    <Button
      data-testid="promote-chat-to-project-button"
      aria-label={t("promoteToProject.ariaLabel")}
      size="1"
      variant="soft"
      color="gray"
      className="cursor-pointer"
      disabled={promote.isPending}
      onClick={handleClick}
    >
      {promote.isPending ? (
        <CircleNotch size={14} weight="bold" className="animate-spin" />
      ) : (
        <FolderPlus size={14} aria-hidden="true" />
      )}
      <Text size="1">{t("promoteToProject.button")}</Text>
    </Button>
  );
}
